import {
  COLOR_PALETTES,
  type ColorPalette,
  type PaletteName,
} from "./color-palettes";

export type StatusColor =
  | "neutral"
  | "brand"
  | "danger"
  | "warning"
  | "success"
  | "information";

export type ComponentColor = StatusColor | PaletteName;

export interface ColorTreatment {
  bold: string;
  subtle: string;
  outline: string;
  text: string;
}

const STATUS_TREATMENTS = {
  neutral: {
    bold: "bg-inverse text-on-inverse",
    subtle: "bg-neutral text-ink",
    outline: "border-line text-ink",
    text: "text-ink",
  },
  brand: {
    bold: "bg-brand-bold text-on-brand",
    subtle: "bg-brand-subtle text-brand",
    outline: "border-brand-line text-brand",
    text: "text-brand",
  },
  danger: {
    bold: "bg-danger-bold text-on-danger",
    subtle: "bg-danger-subtle text-danger",
    outline: "border-danger-line text-danger",
    text: "text-danger",
  },
  warning: {
    bold: "bg-warning-bold text-on-warning",
    subtle: "bg-warning-subtle text-warning",
    outline: "border-warning-line text-warning",
    text: "text-warning",
  },
  success: {
    bold: "bg-success-bold text-on-success",
    subtle: "bg-success-subtle text-success",
    outline: "border-success-line text-success",
    text: "text-success",
  },
  information: {
    bold: "bg-information-bold text-on-information",
    subtle: "bg-information-subtle text-information",
    outline: "border-information-line text-information",
    text: "text-information",
  },
} as const satisfies Record<StatusColor, ColorTreatment>;

const paletteTreatment = (palette: ColorPalette): ColorTreatment => ({
  bold: `${palette.solid} ${palette.solidForeground}`,
  subtle: `${palette.surface} ${palette.foreground}`,
  outline: `${palette.border} ${palette.foreground}`,
  text: palette.foreground,
});

const PALETTE_TREATMENTS = Object.fromEntries(
  Object.entries(COLOR_PALETTES).map(([name, palette]) => [
    name,
    paletteTreatment(palette),
  ]),
) as Record<PaletteName, ColorTreatment>;

export const COLOR_TREATMENTS: Record<ComponentColor, ColorTreatment> = {
  ...STATUS_TREATMENTS,
  ...PALETTE_TREATMENTS,
};
